/**
 * FHIR Task (referral-request) data for the caseload Referrals tab.
 */

import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import type { Practitioner, Task } from '@medplum/fhirtypes';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CANCELLATION_QUEUED_EXT, REFERRAL_REQUEST_TASK_CODE, TASK_TYPE_SYSTEM } from '@unify/fhir-model/constants';
import {
  formatPractitionerPreferredLabel,
  formatPractitionerStaffLabel,
  getPractitionerOrganizationRef,
} from '@unify/fhir-model/resources/practitioner';
import {
  buildReferralRequestTaskCode,
  buildReferralRequestTaskInputs,
  computeReferralRequestMetrics,
  getCancellationQueued,
  getReferralAcceptsInPerson,
  getReferralNoCouplesFamilies,
  getReferralNoMinors,
  getReferralNumberOfClientsRequested,
  getReferralOnlyCashPay,
  isReferralRequestTask,
  latestAssignedAt,
  referralTaskStatusToUiLabel,
} from '../../../fhir-model/resources/referral-task';
import { executeTaskMutation } from '../../../lib/taskLifecycleBots';

export interface ReferralTaskRow {
  /** FHIR Task id */
  id: string;
  requestDate: string;
  requestingProvider: string;
  practice?: string;
  numberOfClients: number;
  referralsSent: number;
  referralsConverted: number;
  referralsDeclined: number;
  referralsReserved: number;
  referralsPending: number;
  availableSlots: number;
  status: 'Pending' | 'In Progress' | 'Completed' | 'Cancelled';
  lastUpdateDate: string;
  referralType?: string;
  notes?: string;
  clientIds: string[];
  clientNames: string[];
  cancellationQueued: boolean;
  sourceTask: Task;
}

export function taskToRow(task: Task, requesterLabel: string): ReferralTaskRow | null {
  if (!task.id || !isReferralRequestTask(task)) return null;
  const authored = task.authoredOn ?? task.meta?.lastUpdated ?? new Date().toISOString();
  const lastAssigned = latestAssignedAt(task) ?? task.meta?.lastUpdated ?? authored;
  const m = computeReferralRequestMetrics(task);
  const practice = task.owner?.display?.trim() || undefined;

  const clientNames = (task.output ?? [])
    .map((o) => o.valueReference?.display)
    .filter((x): x is string => Boolean(x));

  return {
    id: task.id,
    requestDate: authored,
    requestingProvider: task.requester?.display ?? requesterLabel,
    practice,
    numberOfClients: getReferralNumberOfClientsRequested(task),
    referralsSent: m.referralsSent,
    referralsConverted: m.referralsConverted,
    referralsDeclined: m.referralsDeclined,
    referralsReserved: m.referralsReserved,
    referralsPending: m.referralsPending,
    availableSlots: m.availableSlots,
    status: referralTaskStatusToUiLabel(task),
    lastUpdateDate: lastAssigned,
    notes: undefined,
    clientIds: [],
    clientNames,
    cancellationQueued: getCancellationQueued(task),
    sourceTask: task,
  };
}

export interface UseReferralTasksOptions {
  /** When set, query `Task.requester` for this Practitioner (supervisee). When null/undefined, use profile Practitioner. */
  superviseePractitionerId?: string | null;
}

export function useReferralTasks(opts: UseReferralTasksOptions = {}): {
  rows: ReferralTaskRow[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  createReferralRequest: (input: {
    numberOfClients: number;
    acceptsInPerson: boolean;
    noMinors: boolean;
    noCouplesFamilies: boolean;
    onlyCashPay: boolean;
  }) => Promise<void>;
  cancelReferralRequest: (task: Task) => Promise<void>;
} {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const requesterId =
    opts.superviseePractitionerId?.trim() ||
    (profile?.resourceType === 'Practitioner' ? (profile.id as string | undefined) : undefined);

  const requesterLabel =
    profile?.resourceType === 'Practitioner'
      ? formatPractitionerStaffLabel(profile) || 'Provider'
      : 'Provider';

  const envOwnerOrganizationId = import.meta.env.VITE_MEDPLUM_REFERRAL_TASK_OWNER_ORG_ID?.trim() ?? null;
  const profileOwnerOrgRef =
    profile?.resourceType === 'Practitioner'
      ? getPractitionerOrganizationRef(profile as Practitioner)?.reference
      : undefined;
  const ownerOrgRefForCreate =
    profileOwnerOrgRef ??
    (envOwnerOrganizationId ? `Organization/${envOwnerOrganizationId}` : undefined);

  const inFlightRef = useRef(false);
  const loadGenRef = useRef(0);

  const load = useCallback(async () => {
    if (!requesterId) {
      setTasks([]);
      setLoading(false);
      return;
    }
    const gen = ++loadGenRef.current;
    inFlightRef.current = true;
    setLoading(true);
    setError(null);
    try {
      medplum.invalidateSearches('Task');
      const bundle = await medplum.searchResources('Task', {
        requester: `Practitioner/${requesterId}`,
        code: `${TASK_TYPE_SYSTEM}|${REFERRAL_REQUEST_TASK_CODE}`,
        _sort: '-_lastUpdated',
        _count: '100',
      });
      if (gen !== loadGenRef.current) return;
      setTasks((bundle as Task[]).filter(isReferralRequestTask));
    } catch (e) {
      if (gen !== loadGenRef.current) return;
      setError(e instanceof Error ? e.message : 'Failed to load referral requests');
      setTasks([]);
    } finally {
      if (gen === loadGenRef.current) {
        inFlightRef.current = false;
        setLoading(false);
      }
    }
  }, [medplum, requesterId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible' && !inFlightRef.current) {
        void load();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [load]);

  const rows = useMemo(() => {
    const out: ReferralTaskRow[] = [];
    for (const t of tasks) {
      const row = taskToRow(t, requesterLabel);
      if (row) out.push(row);
    }
    return out;
  }, [tasks, requesterLabel]);

  const createReferralRequest = useCallback(
    async (input: {
      numberOfClients: number;
      acceptsInPerson: boolean;
      noMinors: boolean;
      noCouplesFamilies: boolean;
      onlyCashPay: boolean;
    }) => {
      if (!requesterId) throw new Error('No practitioner context for referral request.');
      const requester =
        profile?.resourceType === 'Practitioner' && profile.id === requesterId
          ? profile
          : await medplum.readResource('Practitioner', requesterId);
      const requesterDisplay = formatPractitionerPreferredLabel(requester);
      const draft: Task = {
        resourceType: 'Task',
        status: 'requested',
        intent: 'order',
        code: buildReferralRequestTaskCode(),
        requester: {
          reference: `Practitioner/${requesterId}`,
          ...(requesterDisplay ? { display: requesterDisplay } : {}),
        },
        ...(ownerOrgRefForCreate
          ? {
              owner: { reference: ownerOrgRefForCreate },
              meta: { accounts: [{ reference: ownerOrgRefForCreate }] },
            }
          : {}),
        input: buildReferralRequestTaskInputs({
          numberOfClients: input.numberOfClients,
          acceptsInPerson: input.acceptsInPerson,
          noMinors: input.noMinors,
          noCouplesFamilies: input.noCouplesFamilies,
          onlyCashPay: input.onlyCashPay,
        }),
        authoredOn: new Date().toISOString(),
      };
      await executeTaskMutation(medplum, { action: 'create-payload', task: draft });
      await load();
    },
    [medplum, requesterId, profile, ownerOrgRefForCreate, load],
  );

  const cancelReferralRequest = useCallback(
    async (task: Task) => {
      const m = computeReferralRequestMetrics(task);
      if (m.referralsReserved > 0) {
        const others = (task.extension ?? []).filter((e) => e.url !== CANCELLATION_QUEUED_EXT);
        await executeTaskMutation(medplum, {
          action: 'update-payload',
          task: {
            ...task,
            extension: [...others, { url: CANCELLATION_QUEUED_EXT, valueBoolean: true }],
          },
        });
      } else {
        await executeTaskMutation(medplum, { action: 'cancel', taskId: task.id });
      }
      await load();
    },
    [medplum, load],
  );

  return {
    rows,
    loading,
    error,
    refetch: load,
    createReferralRequest,
    cancelReferralRequest,
  };
}

export function taskPreferencesFromRow(task: Task): {
  acceptsInPerson: boolean;
  noMinors: boolean;
  noCouplesFamilies: boolean;
  onlyCashPay: boolean;
} {
  return {
    acceptsInPerson: getReferralAcceptsInPerson(task) ?? false,
    noMinors: getReferralNoMinors(task) ?? false,
    noCouplesFamilies: getReferralNoCouplesFamilies(task) ?? false,
    onlyCashPay: getReferralOnlyCashPay(task) ?? false,
  };
}
